import {
  CategoryTreeGetAction,
  CategoryTreeGetByVersionAction,
  KalturaCategoryTree,
  KalturaMediaAsset,
  KalturaWatchStatus,
} from '@kaltura-ott/ott-client-types/api/types';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap, pluck } from 'rxjs/operators';

import {
  AssetImageSourceEnum,
  AssetPresentationFormEnum,
  AssetPresentationRatioEnum,
  AssetStructEnum,
  DisplayType,
  KuxCategoriesDTO,
  KuxFilteredCategoriesDTO,
  LCBatchesPresentationOrderEnum,
  MAX_EPG_PERIOD,
  RatingProviderEnum,
  SearchKeys,
  SecondButtonActions,
  TypeOfApplicationLink,
  UIDAPresentationTypeEnum,
  UIDAStructureTypeEnum,
} from '../interfaces';

import { getAssetsByCategoryIds } from './asset';
import {
  categoryItemsIdList,
  setUidaToCategoryTree,
  calculateUiDesignElements,
  calculateAggregatedKSQL,
  prepareCurrentUiDesignElements,
  replaceTemplate,
} from './category.tree.helper';
import { categoryTreeItemsMap } from './category.tree.map';
import { KuXDms } from './dms';
import { kalturaClient } from './kaltura-clent';

const DEFAULT_PROGRAM_REFRESH_FREQUENCY_SEC = 300;

const DEFAULT_PRIME_TIME = 20;
const DEFAULT_ZAPPER_SHOW_DURATION = 5;
const DEFAULT_PLAYER_ONE_CLICK_MOVE_SEC = 30;
const DEFAULT_CONNECTIVITY_TIMEOUT_BUFFER = 20;
const DEFAULT_ANDROID_FALLBACK_PAGE_URL = 'https://play.google.com/store/apps/details?id=';
const DEFAULT_APPLE_FALLBACK_PAGE_URL = 'https://itunes.apple.com/app/';

export const DEFAULT_FAVORITES_TTL = 15;
export const DEFAULT_IDLE_MESSAGE_LIFECYCLE = 10800; // 180 min;

export const DEFAULT_KEEP_ALIVE_BEACONS_FREQUENCY = 10;
export const DEFAULT_IDLE_MESSAGE_DURATION = 60;
export const DEFAULT_FAVORITES_TTL_MS = DEFAULT_FAVORITES_TTL * 60 * 1000;

export enum SpecialWidgetTypeEnum {
  recommendations = 'recommendations',
  search = 'search',
  cast = 'cast',
  logo = 'logo',
}

/**
 *
 * @category Category Tree
 * @param categoryItemId
 * @example
 * ```typescript
 * import {getCategoryTree} from '@kaltura/tvpil';
 *
 * getCategoryTree(categoryItemId)
 *
 *   .pipe()
 * ```
 */
export function getCategoryTree(categoryItemId?: number, versionId?: number): Observable<KalturaCategoryTree> {
  const versionActionData = versionId ? { versionId } : { deviceFamilyId: KuXDms?.deviceFamilyId };
  const action = categoryItemId
    ? new CategoryTreeGetAction({ categoryItemId })
    : new CategoryTreeGetByVersionAction(versionActionData);
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return of(error);
    }),
  );
}

/**
 * @deprecated
 * Get categoryTree with UIDA assets
 *
 * @param categoryItemId: number
 * @param size
 */
export const getFullCategoryTree = (
  categoryItemId?: number,
  size?: number,
  cacheId?: number,
  onlyActive = true,
): Observable<KuxCategoriesDTO | undefined> => {
  const action = categoryItemId
    ? new CategoryTreeGetAction({ categoryItemId })
    : new CategoryTreeGetByVersionAction({ deviceFamilyId: KuXDms?.deviceFamilyId });
  const client = kalturaClient();
  return client.request(action).pipe(
    mergeMap((categoryTree) => {
      if (cacheId && cacheId === categoryTree.id) {
        return of(undefined);
      }
      const categoriesIdList = categoryItemsIdList([categoryTree], onlyActive);
      const pageSize = typeof size !== 'undefined' ? size : categoriesIdList.length;
      return getAssetsByCategoryIds(categoriesIdList, pageSize).pipe(
        pluck('objects'),
        mergeMap((uidaAssets) => setUidaToCategoryTree([categoryTree], uidaAssets as KalturaMediaAsset[])),
      );
    }),
  );
};

export function getMainMenuUiDesignElementsProps(
  categoryTree: KuxCategoriesDTO[],
  parent?: KuxFilteredCategoriesDTO,
): KuxFilteredCategoriesDTO['uiDesignElements'] | undefined {
  const rootMenuTree = categoryTree.find(
    ({ tags }) =>
      tags.UIDA_structure_type === UIDAStructureTypeEnum.menu &&
      tags.UIDA_presentation_type === UIDAPresentationTypeEnum.menu,
  );
  if (rootMenuTree) {
    return calculateUiDesignElements(rootMenuTree?.metas, parent?.uiDesignElements);
  }
}

export function adjustFilteredCategoryTree(categoryTree: KuxFilteredCategoriesDTO) {
  let externalPortalUrl;

  categoryTree?.children.forEach((child) => {
    child.children.forEach((child2) => {
      if (child2.subpageProperty.externalPortalUrl) {
        externalPortalUrl = child2.subpageProperty.externalPortalUrl;
      }
    });
  });

  categoryTree.assetDetailsPageProperty.externalPortalUrl = externalPortalUrl;

  return categoryTree;
}

/**
 * Filter categoryTree by user properties
 *
 * @param categoryTree: KuxCategoriesDTO[]
 * @param parent
 * @param mainMenuUiDesignElements
 */
export function getFilteredCategoryTree({
  categoryTree,
  parent,
  mainMenuUiDesignElements,
  templates,
  predefinedKSQL = '',
}: {
  categoryTree: KuxCategoriesDTO[];
  parent?: KuxFilteredCategoriesDTO;
  mainMenuUiDesignElements?: KuxFilteredCategoriesDTO['uiDesignElements'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  templates?: any;
  predefinedKSQL?: string;
}): KuxFilteredCategoriesDTO[] {
  const mainMenuUiDesignElementsProps =
    mainMenuUiDesignElements ??
    (getMainMenuUiDesignElementsProps(categoryTree, parent) as KuxFilteredCategoriesDTO['uiDesignElements']);

  return categoryTree
    .map((categoryItem) => {
      const { metas, tags } = categoryItem;

      const currentUiDesignElements = prepareCurrentUiDesignElements(
        categoryItem,
        mainMenuUiDesignElementsProps,
        parent?.uiDesignElements,
      );

      const filteredCategoryTree = {
        type: categoryItem.tags.UIDA_structure_type as UIDAStructureTypeEnum,
        subtype: categoryItem.tags.UIDA_presentation_type as UIDAPresentationTypeEnum,
        displayName: (categoryItem.metas['display_name'] || '') as string,
        id: categoryItem.id,
        referenceId: categoryItem.referenceId || categoryItem.id.toString(),
        name: categoryItem.name,
        anchors: (categoryItem.tags['anchors'] || []) as string[],
        images: categoryItem.images,
        channels: categoryItem.channels,
        parentalPinProtected: !!categoryItem.metas['parental_pin_protected'] as boolean,
        parentalAgeGroups: categoryItem.metas['parental_age_groups'] as string,
        presentPlayer: categoryItem.metas['present_player'] as boolean,
        groupBy: categoryItem.metas['group_by'] as string,
        hideLogoutButton: (categoryItem.metas['display_logout_button'] as boolean) || false,
        aggregatedKSQL: calculateAggregatedKSQL(
          metas,
          parent ? parent.aggregatedKSQL : replaceTemplate(predefinedKSQL, templates),
          templates,
        ),
        uiDesignElements: currentUiDesignElements,
        recommendationFields: categoryItem.originTags['recommendation_fields'] || [],
        railImageProperty: {
          assetImageHeight: categoryItem.metas['asset_image_height'] as number,
          assetImageSource: categoryItem.tags['asset_image_source'] as AssetImageSourceEnum,
          assetPresentationRatio: categoryItem.tags['asset_presentation_ratio']
            ? (String(categoryItem.tags['asset_presentation_ratio']) as AssetPresentationRatioEnum)
            : AssetPresentationRatioEnum['1x1'],
          assetPresentationImageType: categoryItem.metas['asset_presentation_image_type'] as number,
          channelPresentationImageType: categoryItem.metas['channel_presentation_image_type'] as number,
        },
        link: {
          typeOfAppLink: categoryItem.tags['type_of_app_link'] as TypeOfApplicationLink,
          inAppLink: categoryItem.metas['in_app_link'] as string,
          targetGridCategoryId: categoryItem.metas['target_grid_category_id'] as string,
          attachedCollectionId: categoryItem.metas['attached_collection_id'] as number,
          displayType: (categoryItem.tags['display_type'] as DisplayType) || DisplayType.text,
        },
        railProperty: {
          presentEntitlementIndicator: categoryItem.metas['present_entitlement_indicator'] as boolean,
          presentFavoriteIndicator: (categoryItem.metas['present_favorite_indicator'] as boolean) || false,
          presentShowMore: categoryItem.metas['present_show_more'] as boolean,
          presentCurrentPrograms: categoryItem.metas['present_current_programs'] as boolean,
          railItemsLimit: categoryItem.metas['rail_items_limit'] as number,
          assetPresentationForm: categoryItem.tags['asset_presentation_form'] as AssetPresentationFormEnum,
          presentRating: (categoryItem.metas['present_rating'] as boolean) || false,
          ratingProvider: categoryItem.metas['rating_provider'] as RatingProviderEnum,
          presentRatingSourceLogo: (categoryItem.metas['present_rating_source_logo'] as boolean) || false,
          programRefreshFrequency:
            (categoryItem.metas['program_refresh_frequency'] as number) || DEFAULT_PROGRAM_REFRESH_FREQUENCY_SEC,
          channelPresentationImageType: categoryItem.metas['channel_presentation_image_type'] as number,
          defaultChannel: (categoryItem.metas['default_channel'] as number) || 0,
          showTopShelfRailUi: (categoryItem.metas['show_top_shelf_rail_ui'] as boolean) || false,
          recordingExpirationBoundary: (categoryItem.metas['recording_expiration_boundary'] as number) || 10,
          newEpisodeBadgeDays: (categoryItem.metas['new_episode_badge_days'] as number) || 7,
          seriesTypeScheduledRecordingsRailTitle:
            (categoryItem.metas['series_type_scheduled_recordings_rail_title'] as string) || '',
          enableFilterByEntitlement: (categoryItem.metas['enable_filter_by_entitlement'] as boolean) || false,
          hideTitles: (categoryItem.metas['hide_titles'] as boolean) || false,
          secondButtonAction: categoryItem.metas['second_button_action'] as SecondButtonActions | undefined,
        },
        subpageProperty: {
          addonCollectionId: categoryItem.metas['addon_collection'] as number,
          allowCancellation: categoryItem.metas['allow_cancellation'] as boolean,
          externalPortalUrl: categoryItem.metas['external_portal_url'] as string,
          heroCollectionId: categoryItem.metas['hero_collection'] as number,
          hidePrices: categoryItem.metas['hide_prices'] as boolean,
          userTypeDenialListForUiComponents: categoryItem.originTags[
            'user_type_denial_list_for_ui_components'
          ] as string[],
          deviceBrandDenialListForUiComponents: categoryItem.originTags[
            'device_brand_denial_list_for_ui_components'
          ] as string[],
          purchaseDeviceBrandDenialList: categoryItem.originTags['purchase_device_brand_denial_list'] as string[],
          notApplicableCollectionId: categoryItem.metas['not_applicable_collection'] as number,
          showManageDevicesButton: (categoryItem.metas['show_manage_devices_button'] as boolean) || false,
          concurrencyPlayerErrorTtl: (categoryItem.metas['concurrency_player_error_ttl'] as number) || 10,
          concurrencyBuymore: (categoryItem.metas['concurrency_buymore'] as boolean) || false,
        },
        epgProperty: {
          primeTime: (categoryItem.metas['prime_time'] as number) || DEFAULT_PRIME_TIME,
          filters: categoryItem.unifiedChannels,
          orderBatches: categoryItem.originTags[
            'linear_channels_batches_presentation_order'
          ] as LCBatchesPresentationOrderEnum[],
          presentationForm:
            (tags['asset_presentation_form'] as AssetPresentationFormEnum) || AssetPresentationFormEnum.landscape,
          presentationRatio:
            (tags['asset_presentation_ratio'] as AssetPresentationRatioEnum) || AssetPresentationRatioEnum['16x9'],
          assetPresentationImageType: metas['asset_presentation_image_type'] as number,
          maxEpgDateBackward: (metas['max_epg_date_backward'] as number) || MAX_EPG_PERIOD,
          maxEpgDateForward: (metas['max_epg_date_forward'] as number) || MAX_EPG_PERIOD,
          zapperShowDurationInSeconds:
            (metas['zapper_show_duration_in_seconds'] as number) || DEFAULT_ZAPPER_SHOW_DURATION,
        },
        assetDetailsPageProperty: {
          struct: String(metas['asset_struct']) || AssetStructEnum.default,
          presentationRatio:
            (tags['asset_presentation_ratio'] as AssetPresentationRatioEnum) || AssetPresentationRatioEnum['16x9'],
          imageHeight: Number(metas['asset_image_height']) || 0,
          imageWidth: 0,
          assetPresentationImageType: metas['asset_presentation_image_type'] as number,
          rentalBadgeBoundary: Number(metas['rental_badge_boundary']) as number,
        },
        asideProperty: {
          presentEntitlementIndicator: categoryItem.metas['present_entitlement_indicator'] as boolean,
          presentFavoriteIndicator: categoryItem.metas['present_favorite_indicator'] as boolean,
          presentRating: categoryItem.metas['present_rating'] as boolean,
          ratingProvider: categoryItem.metas['rating_provider'] as string,
          presentRatingSourceLogo: categoryItem.metas['present_rating_source_logo'] as boolean,
        },
        continueWatchingProperty: {
          statusEqual: (tags['statusEqual'] as KalturaWatchStatus) || KalturaWatchStatus.all,
          typeIn: metas['typeIn'] as string,
          useSuppress: metas['use_suppress'] as boolean,
        },
        children: [] as KuxFilteredCategoriesDTO[],
        channelApiSearchMeta: {
          searchChannelPackagePhrase: metas['search_channel_package_phrase'] as string,
          searchChannelPhrase: metas['search_channel_phrase'] as string,
        },
        mostWatched: {
          presentMostWatchedCount: (metas['present_X_most_watched_count'] as number) || 0,
          presentMostWatchedPeriod: (metas['present_X_most_watched_period'] as number) || 4,
        },
        searchProperty: {
          endSearchTimeInMilliseconds: (metas['end_search_time_in_milliseconds'] as number) || 0,
          recommendationsCollectionId: (metas['recommendations_collection_id'] as number) || 0,
          searchMainDiscoveryAttributes: (tags['search_main_discovery_attributes'] || []) as SearchKeys[],
          maxNumbersOfHistorySearchList: (metas['max_numbers_of_history_search_list'] as number) || 0,
          sortingType: metas['sorting_type'] as SearchKeys,
        },
        imageScreens: {
          imageHeight: categoryItem.metas['asset_image_height'] as number,
          imageHeightCompact:
            (metas['image_height_compact'] as number) || (categoryItem.metas['asset_image_height'] as number),
          imageHeightMedium:
            (metas['image_height_medium'] as number) || (categoryItem.metas['asset_image_height'] as number),
        },
        player: {
          defaultAudioCodes: metas['default_audio_codes'] as string | undefined,
          defaultSubtitlesCodes: metas['default_subtitles_codes'] as string | undefined,
          epgButtonEnabled: (metas['display_btn_epg_navigation'] as boolean | undefined) || false,
          hevcDenyList: metas['HEVC_DENY_LIST'] as string | undefined,
          drmDenyList: metas['DRM_DENY_LIST'] as string | undefined,
          drmCapable: (metas['DRM_capable'] as boolean) ?? true,
          hevcCapable: (metas['HEVC_capable'] as boolean) || false,
          hevcErrorsFallbackLimitation: metas['HEVC_ERRORS_FALLBACK_LIMITAION'] as number | undefined,
          zapperDisplayEnabled: metas['zapperDisplayEnabled'] as boolean | undefined,
          seekWithThumbnailsDenialList: (categoryItem.originTags['seek_with_thumbnails_denial_list'] as string[]) || [],
          keepAliveBeaconsFrequency:
            (metas['keep_alive_beacons_frequency'] as number) || DEFAULT_KEEP_ALIVE_BEACONS_FREQUENCY,
          isIdleMessageEnabled: (metas['enable_idle_message'] as boolean) || false,
          isIdleMessageManagementEnabled: (metas['enable_user_idle_message_management'] as boolean) || false,
          idleMessageLifecycle: (metas['idle_message_lifecycle'] as number) * 60 || DEFAULT_IDLE_MESSAGE_LIFECYCLE,
          idleMessageDuration: (metas['idle_message_duration'] as number) || DEFAULT_IDLE_MESSAGE_DURATION,
          playerOneClickMove: (metas['player_one_click_move_in_sec'] as number) || DEFAULT_PLAYER_ONE_CLICK_MOVE_SEC,
          connectivityTimeoutBuffer:
            (metas['check_connectivity_when_buffer_after_sec'] as number) || DEFAULT_CONNECTIVITY_TIMEOUT_BUFFER,
        },
        favorites: {
          favorites_ttl:
            Number.isInteger(metas['favorites_ttl']) && (metas['favorites_ttl'] as number) > -1
              ? (metas['favorites_ttl'] as number)
              : DEFAULT_FAVORITES_TTL,
        },
        deeplink: {
          androidFallbackPageUrl:
            (metas['android_fallback_page_address'] as string) || DEFAULT_ANDROID_FALLBACK_PAGE_URL,
          appleFallbackPageUrl: (metas['apple_fallback_page_address'] as string) || DEFAULT_APPLE_FALLBACK_PAGE_URL,
        },
      };

      if (categoryItem.isActive) {
        categoryTreeItemsMap.set(filteredCategoryTree.referenceId, filteredCategoryTree);
      }

      return {
        ...filteredCategoryTree,
        children: !categoryItem.children.length
          ? []
          : getFilteredCategoryTree({
              categoryTree: categoryItem.children as KuxCategoriesDTO[],
              parent: filteredCategoryTree,
              mainMenuUiDesignElements,
              templates,
            }),
      };
    })
    .filter(Boolean) as KuxFilteredCategoriesDTO[];
}

export const createFakeCategoryTree = (): KuxFilteredCategoriesDTO => {
  return getFilteredCategoryTree({
    categoryTree: [
      {
        id: Math.ceil(Math.random() * 100000),
        name: '',
        multilingualName: [],
        images: [],
        metas: {},
        tags: {},
        originTags: {},
        channels: [],
        unifiedChannels: [],
        children: [],
        structId: 0,
        virtualAssetId: 0,
        referenceId: '',
      },
    ],
  })[0];
};

export function clearCategoryMap() {
  categoryTreeItemsMap.clear();
}

export function getNewFilteredCategoryTree(args: {
  categoryTree: KuxCategoriesDTO[];
  parent?: KuxFilteredCategoriesDTO;
  mainMenuUiDesignElements?: KuxFilteredCategoriesDTO['uiDesignElements'];
  templates?: any;
  predefinedKSQL?: string;
}) {
  clearCategoryMap();
  return getFilteredCategoryTree(args);
}
